import React, { useState, useEffect } from 'react';
import { Shield, Database, Share2, Lock, Eye, UserCheck, Bell, FileText, HelpCircle, Plus, Trash2, Save } from 'lucide-react';
import { Button } from '../ui/button';

const API_URL = typeof window !== 'undefined' ? window.location.origin : '';

const PrivacyPolicyTab = ({ isDark, authFetch, showToast }) => {
  const [privacyPolicy, setPrivacyPolicy] = useState({
    introduction: '',
    information_collected: '',
    information_usage: '',
    information_sharing: '',
    third_party_disclosure: '',
    security_measures: '',
    user_rights: '',
    cookie_policy: '',
    data_retention: '',
    contact_info: '',
    custom_sections: []
  });
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch privacy policy on mount
  useEffect(() => {
    const fetchPolicy = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/admin/privacy-policy`);
        if (response.ok) {
          const data = await response.json();
          setPrivacyPolicy(prev => ({ ...prev, ...data }));
        }
      } catch (error) {
        console.error('Error fetching privacy policy:', error);
      }
      setLoading(false);
    };
    fetchPolicy();
  }, [authFetch]);

  // Save privacy policy
  const savePrivacyPolicy = async () => {
    setSavingPolicy(true);
    try {
      const response = await authFetch(`${API_URL}/api/admin/privacy-policy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(privacyPolicy)
      });
      
      if (response.ok) {
        showToast('Privacy policy saved successfully');
      } else {
        showToast('Failed to save privacy policy', 'error');
      }
    } catch (error) {
      console.error('Error saving policy:', error);
      showToast('Error saving privacy policy', 'error');
    }
    setSavingPolicy(false);
  };

  // Add custom section
  const addCustomSection = () => {
    setPrivacyPolicy(prev => ({
      ...prev,
      custom_sections: [...(prev.custom_sections || []), { title: '', content: '' }]
    }));
  };

  // Remove custom section
  const removeCustomSection = (index) => {
    setPrivacyPolicy(prev => ({
      ...prev,
      custom_sections: prev.custom_sections.filter((_, i) => i !== index)
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin w-8 h-8 border-4 border-pink-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  const sections = [
    { 
      icon: FileText, 
      label: 'Introduction / はじめに', 
      key: 'introduction', 
      placeholder: 'Introduction text explaining this privacy policy...',
      rows: 4
    },
    { 
      icon: Database, 
      label: 'Information We Collect / 収集する情報', 
      key: 'information_collected', 
      placeholder: 'Describe what personal information you collect: name, email, phone, address, payment information, usage data, etc.',
      rows: 4
    },
    { 
      icon: Eye, 
      label: 'How We Use Information / 情報の利用目的', 
      key: 'information_usage', 
      placeholder: 'Explain how collected information is used: processing orders, communication, improving services, marketing, etc.',
      rows: 4
    },
    { 
      icon: Share2, 
      label: 'Information Sharing / 情報の共有', 
      key: 'information_sharing', 
      placeholder: 'Describe who you share information with: service providers, business partners, legal requirements, etc.',
      rows: 3
    },
    { 
      icon: UserCheck, 
      label: 'Third Party Disclosure / 第三者への開示', 
      key: 'third_party_disclosure', 
      placeholder: 'Detail how and when information is disclosed to third parties, method of disclosure, and for what purposes.',
      rows: 3
    },
    { 
      icon: Lock, 
      label: 'Security Measures / セキュリティ対策', 
      key: 'security_measures', 
      placeholder: 'Describe security practices: SSL encryption, secure servers, access controls, data protection measures, etc.',
      rows: 3
    },
    { 
      icon: Shield, 
      label: 'Your Rights / お客様の権利', 
      key: 'user_rights', 
      placeholder: 'Explain user rights: access to data, correction, deletion, opting out, data portability, etc.',
      rows: 3
    },
    { 
      icon: Bell, 
      label: 'Cookie Policy / Cookieの使用', 
      key: 'cookie_policy', 
      placeholder: 'Describe how cookies are used, types of cookies, and how users can manage them.',
      rows: 3
    },
    { 
      icon: FileText, 
      label: 'Data Retention / 個人情報の保存期間', 
      key: 'data_retention', 
      placeholder: 'Explain how long personal data is retained and criteria for determining retention periods.',
      rows: 2
    },
    { 
      icon: HelpCircle, 
      label: 'Contact Information / お問い合わせ', 
      key: 'contact_info', 
      placeholder: 'Provide contact details for privacy-related inquiries: email, phone, address, etc.',
      rows: 2
    },
  ];

  return (
    <div className="space-y-6" data-testid="privacy-policy-tab">
      <div 
        className="p-6 rounded-lg"
        style={{
          backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : '#f8fafc',
          border: `1px solid ${isDark ? '#333' : '#e2e8f0'}`
        }}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6" style={{ color: isDark ? '#00ffff' : '#0ea5e9' }} />
            <div>
              <h2 className="text-xl font-bold" style={{ color: isDark ? '#ffffff' : '#0f172a' }}>
                プライバシーポリシー
              </h2>
              <p className="text-sm" style={{ color: isDark ? '#888' : '#64748b' }}>
                Privacy Policy (Required for Japanese Data Protection)
              </p>
            </div>
          </div>
          <a 
            href="/privacy-policy" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm underline"
            style={{ color: isDark ? '#00ffff' : '#0ea5e9' }}
            data-testid="view-privacy-page-link"
          >
            View Public Page
          </a>
        </div>

        <div className="space-y-6">
          {sections.map((section) => (
            <div key={section.key}>
              <label className="flex items-center gap-2 text-sm font-medium mb-2" style={{ color: isDark ? '#ccc' : '#475569' }}>
                <section.icon className="w-4 h-4" style={{ color: isDark ? '#ff00ff' : '#d946ef' }} />
                {section.label}
              </label>
              <textarea
                value={privacyPolicy[section.key] || ''}
                onChange={(e) => setPrivacyPolicy(prev => ({ ...prev, [section.key]: e.target.value }))}
                placeholder={section.placeholder}
                rows={section.rows}
                className="w-full p-3 rounded-lg resize-none"
                style={{ 
                  backgroundColor: isDark ? '#1a1a1a' : '#fff', 
                  color: isDark ? '#fff' : '#000', 
                  border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` 
                }}
              />
            </div>
          ))}
        </div>
        
        {/* Custom Sections */}
        <div className="mt-8 pt-6 border-t" style={{ borderColor: isDark ? '#333' : '#e2e8f0' }}>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-sm flex items-center gap-2" style={{ color: isDark ? '#ffff00' : '#eab308' }}>
              <Plus className="w-4 h-4" /> Additional Sections / 追加項目
            </h3>
            <Button onClick={addCustomSection} size="sm" variant="outline" data-testid="add-custom-section-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Section
            </Button>
          </div>
          
          {privacyPolicy.custom_sections?.map((section, index) => (
            <div 
              key={index} 
              className="p-4 rounded-lg mb-4"
              style={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: `1px solid ${isDark ? '#333' : '#e2e8f0'}` }}
            >
              <div className="flex items-center justify-between mb-2">
                <input
                  type="text"
                  value={section.title}
                  onChange={(e) => {
                    const updated = [...privacyPolicy.custom_sections];
                    updated[index].title = e.target.value;
                    setPrivacyPolicy(prev => ({ ...prev, custom_sections: updated }));
                  }}
                  placeholder="Section Title / セクション名"
                  className="flex-1 p-2 rounded mr-2"
                  style={{ backgroundColor: isDark ? '#222' : '#f8fafc', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }}
                />
                <button 
                  onClick={() => removeCustomSection(index)}
                  className="p-2 rounded text-red-500 hover:bg-red-500/10"
                  data-testid={`remove-custom-section-${index}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <textarea
                value={section.content}
                onChange={(e) => {
                  const updated = [...privacyPolicy.custom_sections];
                  updated[index].content = e.target.value;
                  setPrivacyPolicy(prev => ({ ...prev, custom_sections: updated }));
                }}
                placeholder="Section content... / 内容"
                rows={3}
                className="w-full p-2 rounded"
                style={{ backgroundColor: isDark ? '#222' : '#f8fafc', color: isDark ? '#fff' : '#000', border: `1px solid ${isDark ? '#444' : '#e2e8f0'}` }}
              />
            </div>
          ))}
        </div>
        
        {/* Save Button */}
        <Button
          onClick={savePrivacyPolicy}
          disabled={savingPolicy}
          className="w-full mt-6 flex items-center justify-center gap-2"
          style={{ background: isDark ? 'linear-gradient(135deg, #00ffff 0%, #00ff88 100%)' : 'linear-gradient(135deg, #0ea5e9 0%, #10b981 100%)', color: isDark ? '#000' : '#fff' }}
          data-testid="save-privacy-btn"
        >
          <Save className="w-5 h-5" />
          {savingPolicy ? 'Saving...' : 'Save Privacy Policy'}
        </Button>
      </div>
    </div>
  );
};

export default PrivacyPolicyTab;
